import { CalendarCheck, Play, TrendingUp } from 'lucide-react';
import { motion } from 'motion/react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { duration, easing } from '../motion/tokens';

import { HERO, START_FREE_HREF } from './content';
import { usePrefersReducedMotion } from './hooks';
import { OVERVIEW_VIDEO } from './media';
import {
  ChannelBadge,
  ChatBubble,
  FieldRow,
  NotificationCard,
  PipelineStrip,
  ScoreMeter,
  Sparkline,
  TypingBubble,
  WindowChrome,
} from './mockups';
import { MkButton, MkContainer, MkEyebrow, MkPill } from './primitives';
import { VideoModal } from './VideoModal';

/**
 * The hero composition plays once after load, in this order. Every step is a
 * plain index so reduced-motion can jump straight to the end state.
 */
const STEPS = {
  customer: 1,
  typing: 2,
  reply: 3,
  lead: 4,
  score: 5,
  pipeline: 6,
  appointment: 7,
  revenue: 8,
} as const;

const TOTAL_STEPS = 8;

/**
 * The site plays a once-per-session brand curtain over the hero (SiteIntro).
 * When it is showing, hold the sequence back so the first cues are not spent
 * behind it. Same key SiteIntro writes.
 */
function introDelayMs(): number {
  try {
    return sessionStorage.getItem('vaanexa-intro-seen') === '1' ? 0 : 2400;
  } catch {
    return 0;
  }
}

/** Runs the hero sequence on mount rather than on scroll — it is above the fold. */
function useHeroSequence(): number {
  const reduced = usePrefersReducedMotion();
  const [step, setStep] = useState(reduced ? TOTAL_STEPS : 0);

  useEffect(() => {
    if (reduced) {
      setStep(TOTAL_STEPS);
      return;
    }
    let interval = 0;
    const start = window.setTimeout(() => {
      let current = 0;
      interval = window.setInterval(() => {
        current += 1;
        setStep(current);
        if (current >= TOTAL_STEPS) window.clearInterval(interval);
      }, 780);
    }, introDelayMs());

    return () => {
      window.clearTimeout(start);
      window.clearInterval(interval);
    };
  }, [reduced]);

  return step;
}

/** Reveals its children once the sequence reaches `at`, and keeps them mounted. */
function Cue({
  step,
  at,
  children,
  className = '',
}: {
  step: number;
  at: number;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  const shown = step >= at;
  return (
    <motion.div
      className={className}
      initial={false}
      animate={shown ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
      transition={{ duration: duration.standard, ease: easing.entrance }}
      aria-hidden={!shown}
    >
      {children}
    </motion.div>
  );
}

function HeroCopy({ onWatch }: { onWatch: () => void }): JSX.Element {
  const [before, after] = HERO.headline.split(HERO.headlineHighlight);

  return (
    <div className="mx-auto flex max-w-3xl flex-col items-center gap-7 text-center">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: duration.section, ease: easing.entrance }}
      >
        <MkEyebrow>{HERO.eyebrow}</MkEyebrow>
      </motion.div>

      <motion.h1
        className="mk-display text-[var(--mk-ink)]"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: duration.cinematic, ease: easing.entrance, delay: 0.05 }}
      >
        {before}
        <span className="mk-gradient-text">{HERO.headlineHighlight}</span>
        {after}
      </motion.h1>

      <motion.p
        className="max-w-2xl text-lg leading-relaxed text-[var(--mk-ink-soft)] sm:text-xl"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: duration.section, ease: easing.entrance, delay: 0.16 }}
      >
        {HERO.body}
      </motion.p>

      <motion.div
        className="flex flex-col items-center gap-3 sm:flex-row"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: duration.section, ease: easing.entrance, delay: 0.26 }}
      >
        <MkButton href={START_FREE_HREF} variant="primary" withArrow>
          {HERO.primaryCta}
        </MkButton>
        <button
          type="button"
          onClick={onWatch}
          className="group inline-flex items-center justify-center gap-2 rounded-full border border-[var(--mk-line-strong)] bg-[var(--mk-surface)] px-6 py-3 text-sm font-semibold text-[var(--mk-ink)] transition-all duration-200 hover:border-[var(--mk-cyan)] hover:bg-[var(--mk-surface-strong)]"
        >
          <Play className="h-4 w-4" aria-hidden />
          {HERO.secondaryCta}
        </button>
      </motion.div>
    </div>
  );
}

/**
 * The animated product composition. It is a decorative arrangement of real UI
 * shapes — every label is live text, so a screen reader gets the same story.
 */
function HeroComposition({ step }: { step: number }): JSX.Element {
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:items-start">
      {/* Conversation */}
      <WindowChrome
        title="Unified inbox"
        right={<ChannelBadge channel="whatsapp" withLabel />}
        className="min-h-[300px]"
      >
        <div className="flex flex-col gap-3.5 p-4 sm:p-5">
          <Cue step={step} at={STEPS.customer}>
            <ChatBubble from="customer" meta="Rohit · 10:42">
              Hi, do you handle interior design for a 3BHK? What would it cost?
            </ChatBubble>
          </Cue>

          {step >= STEPS.typing && step < STEPS.reply ? <TypingBubble /> : null}

          <Cue step={step} at={STEPS.reply}>
            <ChatBubble from="ai" meta="Vaanexa AI · 10:42">
              Yes, we do full 3BHK interiors. Pricing depends on scope and finish. Would you like a
              rough estimate now, or a design consultation this week?
            </ChatBubble>
          </Cue>

          <Cue step={step} at={STEPS.appointment}>
            <NotificationCard
              icon={<CalendarCheck className="h-4 w-4" />}
              title="Appointment booked — Thu, 4:30 PM"
              body="Design consultation confirmed and added to the team calendar."
              tone="positive"
            />
          </Cue>
        </div>
      </WindowChrome>

      {/* Lead intelligence + pipeline + revenue */}
      <div className="flex flex-col gap-4">
        <Cue step={step} at={STEPS.lead}>
          <WindowChrome title="Lead" right={<MkPill tone="accent">Auto-captured</MkPill>}>
            <div className="px-4 py-2">
              <dl>
                <FieldRow label="Name" value="Rohit Mehra" />
                <FieldRow label="Requirement" value="3BHK interiors" />
                <FieldRow label="Intent" value="High" tone="accent" />
              </dl>
            </div>
            <div className="border-t border-[var(--mk-line)] px-4 py-3.5">
              <ScoreMeter value={step >= STEPS.score ? 87 : 0} />
            </div>
          </WindowChrome>
        </Cue>

        <Cue step={step} at={STEPS.pipeline}>
          <div className="mk-card rounded-2xl p-4">
            <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--mk-ink-muted)]">
              Pipeline
            </p>
            <PipelineStrip activeStage={step >= STEPS.pipeline ? 1 : 0} />
          </div>
        </Cue>

        <Cue step={step} at={STEPS.revenue}>
          <div className="mk-card flex items-center justify-between gap-4 rounded-2xl px-4 py-3.5">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--mk-ink-muted)]">
                Conversations converted
              </p>
              <p className="mt-1 flex items-center gap-1.5 text-lg font-bold text-[var(--mk-ink)]">
                <TrendingUp className="h-4 w-4 text-[var(--mk-positive)]" aria-hidden />
                This week
              </p>
            </div>
            <Sparkline className="h-8 w-24" />
          </div>
        </Cue>
      </div>
    </div>
  );
}

export function Hero(): JSX.Element {
  const step = useHeroSequence();
  const [watching, setWatching] = useState(false);

  return (
    <section
      className="relative overflow-hidden pb-24 pt-28 sm:pb-32 sm:pt-36"
      aria-label="Vaanexa"
    >
      {/* Ambient light: one aurora, one grid, one grain layer. */}
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div className="absolute inset-0 mk-grid" />
        <div className="mk-glow-primary absolute -top-56 left-1/2 h-[640px] w-[1000px] -translate-x-1/2" />
        <div className="mk-glow-cyan absolute right-[6%] top-[38%] h-[380px] w-[420px]" />
        <div className="absolute inset-0 mk-noise" />
      </div>

      <MkContainer className="relative">
        <HeroCopy onWatch={() => setWatching(true)} />

        <motion.div
          className="mx-auto mt-16 max-w-5xl"
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: duration.cinematic, ease: easing.entrance, delay: 0.36 }}
        >
          <HeroComposition step={step} />
        </motion.div>
      </MkContainer>

      <VideoModal
        open={watching}
        onClose={() => setWatching(false)}
        video={OVERVIEW_VIDEO}
        title="Vaanexa in action"
      />
    </section>
  );
}
